import * as React from 'react';
import { cn } from './lib/utils';

const CollapsibleContext = React.createContext(null);

/**
 * Accordion panel with optional single-open behavior and scrollIntoView.
 */
function CollapsiblePanel({
  isOpen: controlledOpen,
  onToggle: controlledToggle,
  header,
  children,
  accentColor,
  scrollOnOpen = true,
  scrollBlock = 'start',
  className,
}) {
  const ctx = React.useContext(CollapsibleContext);
  const ref = React.useRef(null);

  // Use context-provided state if inside a Grid, otherwise use controlled props
  const panelId = React.useId();
  const isOpen = ctx ? ctx.isOpen(panelId) : controlledOpen;
  const onToggle = ctx ? () => ctx.toggle(panelId) : controlledToggle;

  React.useEffect(() => {
    if (isOpen && scrollOnOpen && ref.current) {
      const timer = setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen, scrollOnOpen, scrollBlock]);

  return (
    <div
      ref={ref}
      className={cn('rounded-lg border border-nn-border bg-nn-white overflow-hidden', className)}
      style={accentColor ? { borderLeftWidth: 3, borderLeftColor: accentColor } : undefined}
    >
      <div
        className="cursor-pointer select-none"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle?.(); } }}
      >
        {header}
      </div>
      <div
        className="transition-[max-height,opacity] duration-300 ease-in-out overflow-hidden"
        style={{ maxHeight: isOpen ? 2000 : 0, opacity: isOpen ? 1 : 0 }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Grid container that manages single-open or multi-open accordion state.
 */
function CollapsiblePanelGrid({
  columns = 2,
  expandMode = 'single',
  showToggleAll = false,
  children,
  className,
}) {
  const [openIds, setOpenIds] = React.useState(new Set());

  const toggle = React.useCallback((id) => {
    setOpenIds((prev) => {
      if (expandMode === 'single') {
        return prev.has(id) ? new Set() : new Set([id]);
      }
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [expandMode]);

  const isOpen = React.useCallback((id) => openIds.has(id), [openIds]);

  const colClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
  };

  const allIds = React.Children.toArray(children).map((_, i) => i.toString());
  const allOpen = openIds.size > 0;

  const toggleAll = React.useCallback(() => {
    if (allOpen) {
      setOpenIds(new Set());
    } else {
      setOpenIds(new Set(allIds));
    }
  }, [allOpen, allIds]);

  return (
    <CollapsibleContext.Provider value={{ isOpen, toggle }}>
      {showToggleAll && (
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={toggleAll}
            className="text-body-sm text-aqua hover:text-aqua/80 transition-colors"
          >
            {allOpen ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      )}
      <div className={cn('grid gap-2', colClass[columns] ?? colClass[2], className)}>
        {children}
      </div>
    </CollapsibleContext.Provider>
  );
}

CollapsiblePanel.Grid = CollapsiblePanelGrid;

export { CollapsiblePanel };
